import { useMemo } from 'react'
import { useAppSelector } from '@/store/hooks'
import type { PermissionModule } from '@/types'
import {
  canDeleteModule,
  canEditModule,
  canModuleAction,
  canViewModule,
  hasPermission,
  moduleLevelFromPermissions,
  type ModuleAction,
} from '@/lib/permissions'
import { firstAccessiblePath, moduleForPath } from '@/config/moduleRoutes'

/**
 * Runtime module permission helpers backed by `user.permissions` from login/me.
 */
export function usePermissions() {
  const permissions = useAppSelector(s => s.auth.user?.permissions)

  return useMemo(
    () => ({
      permissions: permissions ?? [],
      has: (name: string) => hasPermission(permissions, name),
      canView: (module: PermissionModule) => canViewModule(permissions, module),
      canEdit: (module: PermissionModule) => canEditModule(permissions, module),
      canDelete: (module: PermissionModule) =>
        canDeleteModule(permissions, module),
      can: (module: PermissionModule, action: ModuleAction) =>
        canModuleAction(permissions, module, action),
      canViewPath: (pathname: string) => {
        const module = moduleForPath(pathname)
        return module === null ? true : canViewModule(permissions, module)
      },
      level: (module: PermissionModule) =>
        moduleLevelFromPermissions(permissions, module),
      firstAccessiblePath: () => firstAccessiblePath(permissions),
    }),
    [permissions]
  )
}
